import { TreeItemData } from '@aibees/elephant/components/checkbox-tree-view';
import type {
  FilterBaseTagData,
  FilterItemTag,
} from '@aibees/elephant/feature/search-filters/components/filter-tags';
import { SearchListType } from '@aibees/elephant/hooks/search-list/types';
import { MetadataType } from '@aibees/elephant/hooks/types';
import { SearchMatchType, SearchTable } from '@aibees/elephant/types';
import { IconName, IconType, SelectItem } from '@aibees/shared/components';
import type { Maybe, Nullable, ReactElement } from '@aibees/shared/definition';
import { SxStyle } from '@aibees/shared/theme';
import type { ColorSchema } from '@aibees/shared/theme/palette';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { ReactNode } from 'react';
import { FieldValues } from 'react-hook-form';
import { GeoLocation } from '../search-table-company';
import { BulkInputFilterValidationResult } from './filters/bulk-input-filter/types';

export interface SearchFiltersProps {
  className?: string;
}

export const enum SearchFilterType {
  BUTTON_GROUP = 'BUTTON_GROUP',
  RADIO_GROUP = 'RADIO_GROUP',
  CHECK_BOX_GROUP = 'CHECK_BOX_GROUP',
  NUMBER_RANGE = 'NUMBER_RANGE',
  DATE_RANGE = 'DATE_RANGE',
  YEAR_RANGE = 'YEAR_RANGE',
  CUSTOM_SELECT = 'CUSTOM_SELECT',
  CUSTOM_SELECT_TREE = 'CUSTOM_SELECT_TREE',
  BULK = 'BULK',
  CHECK_BOX = 'CHECK_BOX',
  TEXT_FIELD = 'TEXT_FIELD',
  TEXT_FIELD_GROUP = 'TEXT_FIELD_GROUP',
  SWITCH = 'SWITCH',
  RADIO = 'RADIO',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  ACCORDION = 'ACCORDION',
  ACCORDION_GROUP = 'ACCORDION_GROUP',
  ACCORDION_SECTION = 'ACCORDION_SECTION',
  TITLE = 'TITLE',
  APPLIED_FILTERS = 'APPLIED_FILTERS',
  NONE = 'NONE',
  ROOT = 'ROOT',
  DURATION = 'DURATION',
  EMPLOYEE_BY_DEPARTMENT = 'EMPLOYEE_BY_DEPARTMENT',
  MIXED = 'MIXED',
  COMPANY_IDS = 'COMPANY_IDS',
  TIME_FRAME = 'TIME_FRAME',
  GROUP_WRAPPER = 'GROUP_WRAPPER',
  CUSTOM_COMPONENT = 'CUSTOM_COMPONENT',
}

export type SearchFilterConfig = {
  labelKey: string;
  valueKey: string;
  secondaryIcon?: string;
};

export interface SearchFilterOptionMetadata
  extends Record<string, Maybe<string | boolean | SearchMatchType[]>> {
  selectAll?: boolean;
  searchMatchItems?: SearchMatchType[];
  searchMatchDefault?: SearchMatchType;
  disabledSearchMatchItems?: SearchMatchType[];
}

export interface SearchFilterOption extends SelectItem {
  icon?: Nullable<string>;
  type: SearchFilterOptionType;
  index?: number;
  metadata?: SearchFilterOptionMetadata;
  selected?: boolean;
  group?: string;
  comingSoon?: boolean;
  valueKey?: string;
  queryKeys?: string[];
  filterFn?: (item: any, userId?: string) => boolean;
}

export type SearchFilterTreeOptions = TreeViewBaseItem<TreeItemData>;

export interface SearchFilter extends Omit<SearchFilterBase, 'category'> {
  searchKey?: string;
  // TODO saman: fix typing issue
  metadata?: {
    includedExcludedLabel?: string;
    color?: ColorSchema;
    labelColor?: ColorSchema;
    icon?: IconType;
    preset?: string;
    jobTitleExpanderVisible?: boolean;
    advancedButtonVisible?: boolean;
    placeholder?: string;
    emptyMessage?: string;
    hideTags?: boolean;
    showCheckbox?: boolean;
    dialog?: {
      title?: string;
      subTitleItemLabel?: string;
      subTitleItemLabelPlural?: string;
      icon?: IconName;
      textArea?: {
        placeholder?: string;
      };
      preTextAreaComponent?: ReactNode;
      postTextAreaComponent?: ReactNode;
      customLeftActions?: ReactNode;
    };
    availability?: {
      requires?: string[];
      required?: boolean;
      info?: Record<SearchTable, string>;
    };
    skipUrlParams?: boolean;
    info?: ReactNode;
    delimiter?: string;
    isPercent?: boolean;
    limit?: {
      max?: {
        size: number;
        message?: string;
      };
    };
    alternate?: {
      icon?: IconType;
      name?: string;
    };
    validation?: {
      type?: FieldValidationType;
      error?: string;
      patternValidation?: (
        inputs: FieldValues
      ) => BulkInputFilterValidationResult;
    };
    singleEntry?: boolean;
    autocomplete?: {
      multiple?: boolean;
      recent?: boolean;
      grouping?: boolean;
      groupByTabs?: boolean;
      limitSelectionToSingleGroup?: boolean;
      selectAllOption?: boolean;
      dontFilterOptions?: boolean;
      onlySearchInResults?: boolean;
      transformSearchValue?: (v: string) => string;
      customOptions?: boolean;
      customOptionsLimit?: number; // maximum number of custom options to show (specific to autocomplete filter with large datasets)
      showSecondaryIcon?: boolean;
      checkboxPosition?: 'left' | 'right';
      iconSx?: SxStyle;
      avartarFallbackIcon?: IconName;
      hideLogoFallback?: boolean;
      allowCustomValuesByEnter?: boolean;
      tabsCountBadge?: boolean;
      searchTriggerMinLength?: number;
    };
    allowParallelItems?: boolean;
    styles?: {
      noBackground?: boolean;
      sx?: SxStyle;
    };
    metaDataApi?: {
      enabled?: boolean;
      key: MetadataType;
      hideCount?: boolean;
    };
    checkbox?: {
      defaultSelected?: boolean;
      comingSoonKeys?: string[];
      showSelectAllOption?: boolean;
      deselectAllExceptionKeys?: string[];
      showOnlyButton?: boolean;
      itemSx?: SxStyle;
    };
    numberRange?: {
      switch?: {
        enabled?: boolean;
        title?: string;
        info?: ReactNode;
      };
      min?: number;
      max?: number;
      startPlaceholder?: string;
      endPlaceholder?: string;
      acceptNegative?: boolean;
      hideTitle?: boolean;
    };
    tabs?: {
      icon?: IconType;
      iconColor?: string;
      defaultOpenIndex?: number;
    };
    bulkInput?: {
      listType?: SearchListType;
    };
  };
  options?: SearchFilterOption[];
  tabs?: string[];
  treeOptions?: SearchFilterTreeOptions[][];
  config?: SearchFilterConfig;
  key?: string;
  field?: string;
  open?: boolean;
  advance?: SearchFilter[];
  nested?: boolean;
  mainItem?: boolean;
  hidden?: boolean;
  layer?: number;
  customComponent?: ReactNode;
}

export interface SearchFilterBase {
  name: string;
  icon?: IconType;
  index: number;
  exactMatch?: {
    enabled?: boolean;
    default?: SearchMatchType;
    customOptions?: SearchMatchType[];
  };
  items?: SearchFilter[];
  type: SearchFilterType;
  category?: FilterCategory[];
  sections?: SearchFilterBaseSections;
  disabled?: boolean;
  comingSoon?: boolean;
  pinned?: boolean;
  layer?: number;
  badge?: FilterBadgeType;
  // isApply?: boolean;
  /* tags?: {
    component?: ReactElement;
    function?: (...args: any) => FilterBaseTagData;
    labelFormatter?: (...args: any) => string;
  }; */
  utils?: {
    tags?: TagUtils;
    valueFormatter?: (...args: any) => string;
    groupValueFormatter?: (...args: any) => string;
    optionsApiFormatter?: (...args: any) => Record<string, any>[];
  };
}

export interface TagUtils {
  component?: ReactElement;
  function?: (...args: any) => FilterBaseTagData;
  getFilterItemTagFunc?: (...args: any) => FilterItemTag;
  labelFormatter?: (...args: any) => string;
  config?: {
    showAsCard?: boolean;
    tagsCardTitle?: string;
  };
}

export type SearchFilterBaseList = SearchFilterBase[];

export type SearchFilterOptionType = 'URL' | 'STRING';

export enum SearchFilterAvailabilityValue {
  AVAILABLE = 'available',
  NOT_AVAILABLE = 'not_available',
}

export type SearchFilterBaseSections = {
  main: number;
  more?: number;
  source?: number;
};

export type onChangeFilterGroupItem = (
  event: any,
  checked: boolean,
  field: string,
  type: SearchFilterType
) => void;

export type AutocompleteOption = {
  label?: string;
  value: string;
  group?: AutocompleteOptionType | string;
  metadata?: {
    category: string;
    description: string;
    logo_url: string;
    domain: string;
    company_name_for_emails: string;
    icon?: IconType;
    records?: number;
    secondaryIcon?: IconType;
    secondaryIconColor?: ColorSchema;
    secondaryIconSize?: number;
    id?: string;
    iconSubtitle?: IconName;
    subtitle?: string;
    date?: string;
    comingSoon?: boolean;
  };
};

export enum AutocompleteOptionType {
  RECENT = 'Recently used',
  BY_SEARCH = 'Search results',
  SELECT_ALL = 'Select all',
}

export enum FieldValidationType {
  NUMBER = 'NUMBER',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  URL = 'URL',
  COMPANY_LINKEDIN_URL = 'COMPANY_LINKEDIN_URL',
  PEOPLE_LINKEDIN_URL = 'PEOPLE_LINKEDIN_URL',
  COMPANY_DOMAIN = 'COMPANY_DOMAIN',
}

export enum FilterCategory {
  RECORD_MANAGEMENT = 'RECORD_MANAGEMENT',
  COMPANY_DATA = 'COMPANY_DATA',
  CONTACT_DATA = 'CONTACT_DATA',
}

export enum FilterBadgeType {
  NEW = 'NEW',
  BETA = 'BETA',
  LOCK = 'LOCK',
}

export enum ClassificationType {
  INDUSTRY = 'INDUSTRY',
  TECHNOLOGY = 'TECHNOLOGY',
  KEYWORD = 'KEYWORD',
  COMPANY_HQ = 'HQ',
  LANGUAGE = 'LANGUAGE',
  SENIORITY = 'SENIORITY',
  HEADLINE = 'HEADLINE',
  SIC_CODE = 'SIC_CODE', // note: this one doesn't have the back-end for now
  SKILL = 'SKILL',
  DEGREE = 'DEGREE',
  FIELD_OF_STUDY = 'FIELD_OF_STUDY',
  CERTIFICATION = 'CERTIFICATION',
}

export enum ClassificationGroups {
  PERSON = 'PERSON',
  COMPANY = 'COMPANY',
}

export interface Classification extends SelectItem {
  name?: string;
  metadata?: Record<string, string>;
}

export interface CountryTimeZone {
  zoneName: string;
  gmtOffset: number;
  gmtOffsetName: string;
  abbreviation: string;
  tzName: string;
}

export interface CountryState {
  name: string;
  geo: [number, number];
}

export interface Country {
  name: string;
  iso2: string;
  capital: string;
  region: string;
  subregion: string;
  timezones: CountryTimeZone[];
  geo: [number, number];
  emoji: string;
  emojiU: string;
  states: CountryState[];
}

export interface LocatioData {
  id: string;
  name: string;
  countryName: string;
  countryCode: string;
  stateName: string;
  stateCode: string;
  rawAddress: string;
  position: GeoLocation;
}
